<!--@format-->
<template>
    <div class="contact-sales-rep-widget">
        <div class="widget-contents-container">
            <div class="widget-header">
                <h3>{{ currentStep.title }}</h3>
                <div class="svg-container widget-close-button" @click="closeWidget">
                    <widget-close-svg />
                </div>
            </div>
            <div class="content-wrapper">
                <div v-if="currentStep.title === 'Assign a Sales Rep'" class="step sales-rep-step">
                    <span class="title"
                        >Select the Salesperson that you would like to assign to this contact.</span
                    >
                    <p class="description">
                        Assign a salesperson to this contact so they can begin a conversation.
                    </p>
                    <div class="contact-form-row">
                        <div class="input-container">
                            <salesperson-search
                                v-model="contact.salesperson"
                                :client-id="contact.clientId"
                                placeholder="Select a Salesperson"
                            />
                        </div>
                    </div>
                </div>
                <div v-else-if="currentStep.title === 'Saving...' || currentStep.title === 'Loading...'" class="step loading-step">
                    <loading-screen-svg />
                </div>
            </div>

            <div class="widget-footer">
                <div class="cancel-container">
                    <app-button class="transparent-btn" @click="closeWidget"> Cancel </app-button>
                </div>
                <div class="action-container">
                    <app-button :loading="currentStep.loading" @click="currentStep.action">
                        {{ currentStep.buttonText }}
                    </app-button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
const AppButton = () => import('@/components/common/app-button.vue');
const SalespersonSearch = () => import('@/components/user/salesperson-search');
const WidgetCloseSvg = () => import('@/components/svg/quick-add/widget-close-svg.vue');
const LoadingScreenSvg = () => import('@/components/svg/loading-screen-svg');

import EventBus from '@/scripts/event-bus.js';
import { CustomFieldsServiceMixin } from '@/mixins/custom-fields-service-mixin';

export default {
    name: 'contact-sales-rep-widget',

    components: {
        'app-button': AppButton,
        'widget-close-svg': WidgetCloseSvg,
        'salesperson-search': SalespersonSearch,
        'loading-screen-svg': LoadingScreenSvg,
    },

    mixins: [CustomFieldsServiceMixin],

    props: {
        widget: {
            type: Object,
            required: true,
        },
    },

    data() {
        return {
            contact: {
                id: null,
                firstName: null,
                clientId: null,
                salesperson: null,
            },

            currentStep: null,
            steps: {
                contact: {
                    title: 'Assign a Sales Rep',
                    buttonText: 'Save',
                    loading: false,
                    action: this.saveContact,
                },

                saving: {
                    title: 'Saving...',
                    buttonText: 'Saving',
                    loading: true,
                    action: () => {
                        return null;
                    },
                },

                loading: {
                    title: 'Loading...',
                    buttonText: 'Loading...',
                    loading: true,
                    action: () => {
                        return null;
                    },
                },
            },
        };
    },

    created() {
        this.contact.id = this.widget.options.contactId;
        this.goToStep('loading');

        this.getContact()
            .then(() => this.goToStep('contact'))
            .catch(() => this.closeWidget());
    },

    methods: {
        getContact() {
            return new Promise((resolve, reject) => {
                this.$api
                    .get(
                        `/service/Contacts?$filter=Id eq ${this.contact.id}&$expand=SalesPerson,ContactStatus,Company,ListContacts($expand=List) `,
                    )
                    .then((response) => {
                        this.contact.clientId = response.data.value[0].ClientId;
                        this.contact.firstName = response.data.value[0].FirstName;

                        if (response.data.value[0].SalesPerson) {
                            this.contact.salesperson = {
                                id: response.data.value[0].SalesPerson.Id,
                                profileImageUrl:
                                    response.data.value[0].SalesPerson.ProfileImageUrl ||
                                    require('@/assets/images/avatar.png'),
                                name: this.$helpers.getName(
                                    response.data.value[0].SalesPerson.FirstName,
                                    response.data.value[0].SalesPerson.LastName,
                                ),
                                title: response.data.value[0].SalesPerson.Title,
                            };
                        }
                        resolve();
                    })
                    .catch((error) => {
                        this.$helpers.showFailNotification('Failed to get your contact data', {
                            error,
                        });
                        reject();
                    });
            });
        },

        saveContact() {
            this.currentStep = this.steps['saving'];

            this.patchContact()
                .then(() => {
                    this.$helpers.showSuccessNotification(
                        `Your changes to ${this.contact.firstName} have been saved.`,
                    );
                    EventBus.$emit('contact:update');

                    this.closeWidget();
                })
                .catch(() => {
                    this.goToStep('contact');
                });
        },

        patchContact() {
            var changes = [
                {
                    name: 'SalesPersonId',
                    value: this.contact.salesperson ? this.contact.salesperson.id : null,
                },
            ];

            return new Promise((resolve, reject) => {
                this.$api
                    .patch(`/api/Contacts/${this.contact.id}`, changes)
                    .then(resolve)
                    .catch((error) => {
                        this.$helpers.showFailNotification('Failed to assign your Sales Rep.', {
                            error,
                        });
                        reject();
                    });
            });
        },

        closeWidget() {
            EventBus.$emit('close-widget', this.widget);
        },

        goToStep(step) {
            this.currentStep = this.steps[step];
        },
    },
};
</script>

<style>
.contact-sales-rep-widget div.content-wrapper {
    padding: 26px 36px;
}

.contact-sales-rep-widget .content-wrapper .sales-rep-step .title {
    color: #382153;
    font: normal normal bold 18px/24px Nunito;
    letter-spacing: 0px;
    margin-bottom: 4px;
    opacity: 1;
    text-align: left;
}

.contact-sales-rep-widget .content-wrapper .sales-rep-step .description {
    color: #382153;
    font: normal normal normal 16px/24px Roboto;
    letter-spacing: 0px;
    margin-bottom: 24px;
    opacity: 1;
    text-align: left;
}

.contact-sales-rep-widget .step {
    width: 100%;
}

.contact-sales-rep-widget .step.loading-step {
    align-items: center;
    display: flex;
    justify-content: center;
}

.contact-sales-rep-widget .step.step.loading-step .loading-screen {
    margin: 0;
}

.contact-sales-rep-widget .step .contact-form-row {
    display: flex;
    justify-content: space-between;
    margin-left: -10px;
    margin-right: -10px;
}

.contact-sales-rep-widget .step .contact-form-row .input-container {
    display: flex;
    flex-direction: column;
    margin-bottom: 24px;
    flex-grow: 1;
    margin-left: 10px;
    margin-right: 10px;
}

.contact-sales-rep-widget .step .contact-form-row .description-container {
    width: 100%;
}

.contact-sales-rep-widget .step .contact-form-row .input-container textarea {
    border: 1px solid #e9e9f0;
    width: 100%;
    max-width: 100%;
    min-height: 150px;
}

.contact-sales-rep-widget .action-container {
    display: flex;
}

.contact-sales-rep-widget .action-container .btn {
    margin-left: 15px;
}

.contact-sales-rep-widget .cancel-container .btn.transparent-btn {
    background: transparent;
    color: #877d88;
}

.contact-sales-rep-widget .cancel-container .btn.transparent-btn:hover {
    background: #e1e0e5;
}

.contact-sales-rep-widget .action-container .btn.transparent-btn {
    background: transparent;
    color: #18aeff;
}

.contact-sales-rep-widget .action-container .btn.transparent-btn:hover {
    background: #dbedfa;
}

.contact-sales-rep-widget .input-container {
    width: 100%;
}
</style>
